import {Injectable, Injector} from '@angular/core';
import {Observable, throwError} from 'rxjs';


import {BaseService} from '../base/base.service';

import {Contract} from '../../models/nabavke/contract';
import {Query} from '../../models/query/query';
import {Criterion} from '../../models/query/criterion';
import {WebMethodResult} from '../../models/web-method-result';
import {AppModulePath} from '../../models/app-module-path';

import {ContractItem} from '../../models/nabavke/contract-item';
import {Item} from '../../models/lookups/item';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {PlatformLocation} from '@angular/common';
import {catchError, map} from 'rxjs/operators';
import {isNil} from 'lodash';

@Injectable()
export class ContractService extends BaseService {

  private listUrl = '/api/contract/list';
  private saveUrl = '/api/contract/save';
  private deleteUrl = 'api/contract/delete/';
  private countUrl = 'api/contract/count';
  private byIdUrl = 'api/contract/byId';
  private findByIdUrl = 'api/contract/findById';
  private createSameUrl = 'api/contract/createSame';

  constructor(protected injector: Injector,
              protected http2: HttpClient,
              protected platformLocation: PlatformLocation) {
    super(injector, http2, platformLocation);
  }

  list(qry: Query): Observable<Contract[]> {
    const url = this.listUrl + '?q=' + encodeURIComponent(JSON.stringify(qry));
    return this.get<Contract[]>(url, AppModulePath.CONTRACT).pipe(
      map(res => this.extractData(res)),
      catchError(this.handleError));
  }

  // without items:
  byId(contractId: string): Observable<Contract> {
    return this.get<Contract>(this.byIdUrl + '/' + contractId, AppModulePath.CONTRACT).pipe(
      map(res => {
        const data = this.extractData(res);
        return data.length > 0 ? data[0] : null;
      }),
      catchError(this.handleError));
  }

  // with items:
  findById(contractId: string): Observable<Contract> {
    return this.get<Contract>(this.findByIdUrl + '/' + contractId, AppModulePath.CONTRACT).pipe(
      map(res => {
        const data = this.extractData(res);
        return data.length > 0 ? data[0] : null;
      }),
      catchError(this.handleError));
  }

  count(qry: Query): Observable<number> {
    const url = this.countUrl + '?q=' + encodeURIComponent(JSON.stringify(qry));
    return this.get<number>(url, AppModulePath.CONTRACT).pipe(
      map(res => res),
      catchError(this.handleError));
  }

  save(contract: Contract): Observable<Contract> {
    return this.post<Contract>(this.saveUrl, AppModulePath.CONTRACT, contract).pipe(
      map(res => {
        const data = this.extractData(res);
        return data.length > 0 ? data[0] : null;
      }),
      catchError(this.handleError));
  }

  delete(id: string): Observable<WebMethodResult> {
    return this._delete<WebMethodResult>(this.deleteUrl + id, AppModulePath.CONTRACT).pipe(
      map(res => new WebMethodResult(res)),
      catchError((error: any) => throwError(error.error || 'Server error')));
  }

  extractData(res: any): Contract[] {
    const data = res || [];
    if (data instanceof Array) {
      return data.map((c: Contract) => {
        c = new Contract(c);
        // Backend can return null/empty date fields; avoid converting them to 1970-01-01.
        c.referenceDate = this.toDateOrNull(c.referenceDate);
        c.dateFrom = this.toDateOrNull(c.dateFrom);
        c.dateTo = this.toDateOrNull(c.dateTo);
        c.createdAt = this.toDateOrNull(c.createdAt);
        c.updatedAt = this.toDateOrNull(c.updatedAt);
        c.deletedAt = this.toDateOrNull(c.deletedAt);
        c.contractItems = (c.contractItems || []).map((ci: ContractItem) => {
          ci = new ContractItem(ci);
          if (ci.item) {
            ci.item = new Item(ci.item);
          }
          return ci;
        });
        return c;
      });
    } else {
      const con = new Contract(data);
      con.referenceDate = this.toDateOrNull(data.referenceDate);
      con.dateFrom = this.toDateOrNull(data.dateFrom);
      con.dateTo = this.toDateOrNull(data.dateTo);
      con.createdAt = this.toDateOrNull(data.createdAt);
      con.updatedAt = this.toDateOrNull(data.updatedAt);
      con.deletedAt = this.toDateOrNull(data.deletedAt);

      con.contractItems = (con.contractItems || []).map((ci: ContractItem) => {
        ci = new ContractItem(ci);
        if (ci.item) {
          ci.item = new Item(ci.item);
        }
        return ci;
      });
      // data.push(con);
      return [con];
    }
  }

  createSame(contract: Contract): Observable<Contract> {
    return this.post<Contract>(this.createSameUrl, AppModulePath.CONTRACT, contract).pipe(
      map(res => {
        const data = this.extractData(res);
        return data.length > 0 ? data[0] : null;
      }),
      catchError(this.handleError));
  }

  export(id: string): any {
    const qry = new Query();
    qry.and().and(Criterion.equal(Contract.TABLENAME, Contract.ID, id));

    return this.downloadFile('/api/contract/report/ugovor', AppModulePath.CONTRACT, qry).pipe(
      map((res) => {
        return new Blob([res], {type: 'application/pdf'});
      }),
      catchError(this.noData));
  }

  public noData(error: HttpResponse<any>) {
    alert('Nema artikala za izvoz.');
    return throwError(error || 'Server error');
  }

  private toDateOrNull(value: any): Date | null {
    if (isNil(value) || value === '') {
      return null;
    }

    const date = new Date(value);
    return isNaN(date.valueOf()) ? null : date;
  }

}
